<script lang="ts">
	import { format } from 'svelte-i18n';

	import type { Device } from 'src/types/Device';
	import type { PVSetupTypes } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';

	import { devices, devices_status, home_data, rooms } from 'src/stores';

	import { Collapse, LabelV2, SelectCardV2, Stack, Text } from '@Shelly/shelly-design-system';

	import { getDeviceImage } from 'src/helpers/device_state_view_helpers';
	import { devices_config } from '@devices/index';
	import {
		getDeviceDescription,
		groupDevicesByRoom,
		isDeviceOnline,
	} from './main-energy-meter-helpers';
	import {
		getConfiguredMeterIdsByRole,
		isEligibleSolarMeterDevice,
		type PvConfigurations,
		type SolarMeterRole,
	} from './solar-setup-helpers';

	export let selectedDeviceIds: string[] = [];
	export let excludedDeviceIds: string[] = [];
	export let setupType: PVSetupTypes;
	export let role: SolarMeterRole = 'grid';
	export let mainEnergyMeterId: string | null = null;
	export let idPrefix = 'solar-meter';

	let roomExpanded: Record<string, boolean> = {};
	let seededKey = '';

	$: resolveRoomName = (roomId: number | undefined): string =>
		$rooms?.[roomId]?.name || $format('text_no_room_label', { default: 'Other' });
	$: eligibleDevices = (Object.values($devices) as Device[]).filter(
		(device) =>
			isEligibleSolarMeterDevice(
				device,
				setupType,
				role,
				Boolean(devices_config[device.type]?.isPM),
			) && !excludedDeviceIds.includes(device.id),
	);
	$: roomGroups = groupDevicesByRoom(eligibleDevices, $devices_status, resolveRoomName, idPrefix);
	$: configuredSolarMeterIds = getConfiguredMeterIdsByRole(
		$home_data?.pv_configurations as PvConfigurations | undefined,
		'solar',
	);
	$: groupKey = `${setupType}-${role}-${idPrefix}`;
	$: if (seededKey !== groupKey && roomGroups.length > 0) {
		roomExpanded = Object.fromEntries(roomGroups.map((roomGroup) => [roomGroup.id, true]));
		seededKey = groupKey;
	}

	const toggleRoom = (roomId: string): void => {
		roomExpanded = { ...roomExpanded, [roomId]: !roomExpanded[roomId] };
	};

	const handleSelectDevice = (device: Device): void => {
		if (selectedDeviceIds.includes(device.id)) {
			selectedDeviceIds = selectedDeviceIds.filter((id) => id !== device.id);
			return;
		}
		selectedDeviceIds = [...selectedDeviceIds, device.id];
	};
</script>

{#if roomGroups.length === 0}
	<Text class="text-color-muted mb-m">
		{$format('description_no_energy_meters', { default: 'No compatible energy meters found.' })}
	</Text>
{:else}
	<Stack gap="s" class="mb-m">
		{#each roomGroups as roomGroup (roomGroup.id)}
			<Collapse
				id={roomGroup.id}
				title={roomGroup.roomName}
				expanded={!!roomExpanded[roomGroup.id]}
				on:click={() => toggleRoom(roomGroup.id)}
			>
				<div class="device-grid">
					{#each roomGroup.devices as device (device.id)}
						{@const online = isDeviceOnline(device, $devices_status)}
						<SelectCardV2
							type="horizontal"
							variant="checkbox"
							class="width-full"
							title={device.name || device.type}
							descriptiveText={getDeviceDescription(device.type, online, $format)}
							image={getDeviceImage(device) ?? ''}
							icon="fas fa-bolt"
							backgroundColor="color-base"
							selected={selectedDeviceIds.includes(device.id)}
							on:click={() => handleSelectDevice(device)}
						>
							{#if mainEnergyMeterId === device.id}
								<LabelV2
									slot="rightBadge"
									size="s"
									value={$format('label_main_energy_meter', {
										default: 'Main Energy Meter',
									})}
								/>
							{:else if configuredSolarMeterIds.includes(device.id)}
								<LabelV2
									slot="rightBadge"
									size="s"
									value={$format('label_already_solar_meter', {
										default: 'Already a solar meter',
									})}
								/>
							{/if}
						</SelectCardV2>
					{/each}
				</div>
			</Collapse>
		{/each}
	</Stack>
{/if}

<style>
	.device-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: var(--spacing-xs);
	}

	.device-grid > :global(.select-card-v2-container),
	.device-grid > :global(.select-card-container) {
		min-width: 0;
	}

	@media (max-width: 639px) {
		.device-grid {
			grid-template-columns: 1fr;
		}
	}
</style>
